import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import FormFieldV2 from './FormFieldV2';
import ContactCategorySelectV2 from './ContactCategorySelectV2';
import useSubmitLead from '../../hooks/useSubmitLead';
import V2_CONTACT_CATEGORIES from '../../data/v2ContactCategories';

const COPY = {
  en: {
    fields: {
      name: 'Your name',
      email: 'Email',
      message: 'Message',
      business_name: 'Business or organization',
      phone: 'Phone',
      preferred_contact: 'Preferred contact method',
      preferred_contact_options: { email: 'Email', phone: 'Phone', either: 'Either' },
      how_heard: 'How did you hear about illumi.dev?',
      best_time_to_reach: 'Best time to reach you',
      preferred_contact_placeholder: '— Select —',
    },
    hints: {
      message: '10–4000 characters.',
    },
    consent: 'I agree that illumi.dev may use the information provided to respond to this inquiry.',
    phone_prompt: "You've chosen phone as your preferred method — adding a phone number will help us reach you.",
    submit: 'Send message',
    sending: 'Sending…',
    error_summary: 'Please fix the highlighted fields.',
    errors: {
      required: 'This field is required.',
      name_short: 'Please enter your full name (at least 2 characters).',
      email_invalid: 'Please enter a valid email address.',
      reason_invalid: 'Please select a reason for contact.',
      message_range: 'Message must be between 10 and 4000 characters.',
      consent_required: 'You must agree before sending your message.',
    },
    server: {
      generic: 'Something went wrong. Please try again in a moment.',
      rate_limited: 'We received several messages from this device recently. Please try again in a little while.',
      server_error: "We couldn't send your message right now. Please try again in a moment.",
    },
    success: {
      heading: 'Message received.',
      body: "Your message is in. We'll review it and follow up using the contact information you provided.",
      ref_label: 'Reference',
      back: 'Back to illumi.dev',
    },
    optional: 'Optional',
  },
  es: {
    fields: {
      name: 'Tu nombre',
      email: 'Correo electrónico',
      message: 'Mensaje',
      business_name: 'Negocio u organización',
      phone: 'Teléfono',
      preferred_contact: 'Método de contacto preferido',
      preferred_contact_options: { email: 'Correo', phone: 'Teléfono', either: 'Cualquiera' },
      how_heard: '¿Cómo supiste de illumi.dev?',
      best_time_to_reach: 'Mejor horario para contactarte',
      preferred_contact_placeholder: '— Selecciona —',
    },
    hints: {
      message: '10 a 4000 caracteres.',
    },
    consent: 'Acepto que illumi.dev utilice la información proporcionada para responder a esta consulta.',
    phone_prompt: 'Elegiste teléfono como tu método preferido — incluir un número nos ayudará a contactarte.',
    submit: 'Enviar mensaje',
    sending: 'Enviando…',
    error_summary: 'Por favor corrige los campos marcados.',
    errors: {
      required: 'Este campo es obligatorio.',
      name_short: 'Ingresa tu nombre (al menos 2 caracteres).',
      email_invalid: 'Ingresa un correo electrónico válido.',
      reason_invalid: 'Selecciona un motivo del contacto.',
      message_range: 'El mensaje debe tener entre 10 y 4000 caracteres.',
      consent_required: 'Debes aceptar antes de enviar tu mensaje.',
    },
    server: {
      generic: 'Ocurrió un error. Inténtalo de nuevo en un momento.',
      rate_limited: 'Recibimos varios mensajes desde este dispositivo recientemente. Inténtalo de nuevo en un momento.',
      server_error: 'No pudimos enviar tu mensaje en este momento. Inténtalo de nuevo en un momento.',
    },
    success: {
      heading: 'Mensaje recibido.',
      body: 'Recibimos tu mensaje. Lo revisaremos y te responderemos usando la información de contacto que compartiste.',
      ref_label: 'Referencia',
      back: 'Volver a illumi.dev',
    },
    optional: 'Opcional',
  },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const shortId = (id) => (typeof id === 'string' && id.length >= 8 ? id.slice(0, 8) : (id || ''));

const ContactFormV2 = ({ lang = 'en' }) => {
  const t = COPY[lang] || COPY.en;
  const startedAtRef = useRef(Date.now());
  const [values, setValues] = useState({
    name: '',
    email: '',
    reason: '',
    message: '',
    business_name: '',
    phone: '',
    preferred_contact: '',
    how_heard: '',
    best_time_to_reach: '',
    contact_consent: false,
    website: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [showSummary, setShowSummary] = useState(false);
  const [serverErrorKey, setServerErrorKey] = useState(''); // '' | 'generic' | 'rate_limited' | 'server_error'
  const { submit, pending, response, error: submitError, status: submitStatus } = useSubmitLead('/api/submissions/contact');

  // Map hook error → localized server error key. Runs whenever the hook
  // transitions to 'error' so the message reflects the actual HTTP status.
  useEffect(() => {
    if (submitStatus !== 'error') return;
    const message = submitError || '';
    if (/\b429\b/.test(message)) setServerErrorKey('rate_limited');
    else if (/\b5\d\d\b/.test(message)) setServerErrorKey('server_error');
    else setServerErrorKey('generic');
  }, [submitStatus, submitError]);

  const isSuccess = response && response.success === true;
  const errorSummaryRef = useRef(null);

  const setField = (name) => (e) => {
    const nextValue = e && e.target ? (
      e.target.type === 'checkbox' ? e.target.checked : e.target.value
    ) : e;
    setValues((prev) => ({ ...prev, [name]: nextValue }));
  };

  const validate = () => {
    const errs = {};
    if (!values.name || values.name.trim().length < 2) errs.name = t.errors.name_short;
    if (!values.email || !EMAIL_RE.test(values.email.trim())) errs.email = t.errors.email_invalid;
    const validReason = V2_CONTACT_CATEGORIES.some((c) => c.value === values.reason);
    if (!validReason) errs.reason = t.errors.reason_invalid;
    const msgLen = (values.message || '').trim().length;
    if (msgLen < 10 || msgLen > 4000) errs.message = t.errors.message_range;
    if (!values.contact_consent) errs.contact_consent = t.errors.consent_required;
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerErrorKey('');
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      setShowSummary(true);
      requestAnimationFrame(() => {
        if (errorSummaryRef.current) errorSummaryRef.current.focus();
      });
      return;
    }
    setShowSummary(false);

    const elapsed_ms = Date.now() - startedAtRef.current;
    const payload = {
      name: values.name.trim(),
      email: values.email.trim(),
      reason: values.reason,
      message: values.message.trim(),
      contact_consent: true,
      preferred_language: lang,
      business_name: values.business_name.trim() || null,
      phone: values.phone.trim() || null,
      preferred_contact: values.preferred_contact || null,
      how_heard: values.how_heard.trim() || null,
      best_time_to_reach: values.best_time_to_reach.trim() || null,
      website: values.website, // honeypot
      elapsed_ms,
    };

    const result = await submit(payload);
    // Server error mapping is handled by the useEffect on submitStatus/submitError above.
    // No-op here — leaves the hook to drive serverErrorKey.
    void result;
  };

  const showPhonePrompt = values.preferred_contact === 'phone' && !values.phone.trim();

  const summaryList = useMemo(() => {
    return Object.entries(fieldErrors).map(([field, msg]) => ({ field, msg }));
  }, [fieldErrors]);

  // Success card — new submission OR duplicate OR honeypot (which returns no ids)
  if (isSuccess) {
    const submissionId = response.submission_id || null;
    return (
      <div className="iv2-contact-success" role="status" data-testid="iv2-contact-success">
        <h2 className="iv2-contact-success-heading" data-testid="iv2-contact-success-heading">
          {t.success.heading}
        </h2>
        <p className="iv2-contact-success-body">{t.success.body}</p>
        {submissionId && (
          <p className="iv2-contact-success-ref" data-testid="iv2-contact-success-ref">
            <span className="iv2-contact-success-ref-label">{t.success.ref_label}:</span>{' '}
            <code>{shortId(submissionId)}</code>
          </p>
        )}
        <Link
          to={`/v2?lang=${lang}`}
          className="iv2-btn iv2-btn-secondary"
          data-testid="iv2-contact-back"
        >
          {t.success.back}
        </Link>
      </div>
    );
  }

  return (
    <form
      className="iv2-contact-form"
      onSubmit={handleSubmit}
      noValidate
      data-testid="iv2-contact-form"
    >
      {showSummary && summaryList.length > 0 && (
        <div
          className="iv2-contact-error-summary"
          role="alert"
          tabIndex={-1}
          ref={errorSummaryRef}
          data-testid="iv2-contact-error-summary"
        >
          <p className="iv2-contact-error-summary-title">{t.error_summary}</p>
          <ul>
            {summaryList.map(({ field, msg }) => (
              <li key={field}>{msg}</li>
            ))}
          </ul>
        </div>
      )}

      {serverErrorKey && (
        <div
          className="iv2-contact-server-error"
          role="alert"
          data-testid="iv2-contact-server-error"
        >
          {t.server[serverErrorKey] || t.server.generic}
        </div>
      )}

      <FormFieldV2
        id="name"
        label={t.fields.name}
        required
        value={values.name}
        onChange={setField('name')}
        error={fieldErrors.name}
        autoComplete="name"
      />

      <FormFieldV2
        id="email"
        label={t.fields.email}
        type="email"
        required
        value={values.email}
        onChange={setField('email')}
        error={fieldErrors.email}
        autoComplete="email"
      />

      <ContactCategorySelectV2
        lang={lang}
        value={values.reason}
        onChange={setField('reason')}
        error={fieldErrors.reason}
      />

      <FormFieldV2
        id="message"
        as="textarea"
        label={t.fields.message}
        hint={t.hints.message}
        required
        rows={6}
        maxLength={4000}
        value={values.message}
        onChange={setField('message')}
        error={fieldErrors.message}
      />

      <FormFieldV2
        id="business_name"
        label={`${t.fields.business_name} (${t.optional})`}
        value={values.business_name}
        onChange={setField('business_name')}
        autoComplete="organization"
        maxLength={160}
      />

      <FormFieldV2
        id="phone"
        label={`${t.fields.phone} (${t.optional})`}
        type="tel"
        value={values.phone}
        onChange={setField('phone')}
        autoComplete="tel"
        maxLength={40}
      />

      <FormFieldV2
        id="preferred_contact"
        as="select"
        label={`${t.fields.preferred_contact} (${t.optional})`}
        value={values.preferred_contact}
        onChange={setField('preferred_contact')}
      >
        <option value="">{t.fields.preferred_contact_placeholder}</option>
        <option value="email">{t.fields.preferred_contact_options.email}</option>
        <option value="phone">{t.fields.preferred_contact_options.phone}</option>
        <option value="either">{t.fields.preferred_contact_options.either}</option>
      </FormFieldV2>

      {showPhonePrompt && (
        <p className="iv2-contact-inline-note" role="note" data-testid="iv2-contact-phone-prompt">
          {t.phone_prompt}
        </p>
      )}

      <FormFieldV2
        id="how_heard"
        label={`${t.fields.how_heard} (${t.optional})`}
        value={values.how_heard}
        onChange={setField('how_heard')}
        maxLength={160}
      />

      <FormFieldV2
        id="best_time_to_reach"
        label={`${t.fields.best_time_to_reach} (${t.optional})`}
        value={values.best_time_to_reach}
        onChange={setField('best_time_to_reach')}
        maxLength={160}
      />

      {/* Honeypot — visually hidden, kept out of tab/AT tree */}
      <div className="iv2-hp" aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input
          id="website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={values.website}
          onChange={setField('website')}
          data-testid="iv2-input-website"
        />
      </div>

      <div className={`iv2-field iv2-field-consent ${fieldErrors.contact_consent ? 'iv2-field-invalid' : ''}`}>
        <label className="iv2-consent-label" htmlFor="contact_consent">
          <input
            id="contact_consent"
            name="contact_consent"
            type="checkbox"
            checked={values.contact_consent}
            onChange={setField('contact_consent')}
            aria-invalid={fieldErrors.contact_consent ? 'true' : 'false'}
            data-testid="iv2-input-contact_consent"
          />
          <span>{t.consent}</span>
        </label>
        {fieldErrors.contact_consent && (
          <span className="iv2-field-error" role="alert" data-testid="iv2-error-contact_consent">
            {fieldErrors.contact_consent}
          </span>
        )}
      </div>

      <div className="iv2-contact-actions">
        <button
          type="submit"
          className="iv2-btn iv2-btn-primary"
          disabled={pending}
          data-testid="iv2-contact-submit"
        >
          {pending ? t.sending : t.submit}
        </button>
      </div>
    </form>
  );
};

export default ContactFormV2;
